import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Space,
  Tabs,
  message,
  Spin,
  Row,
  Col,
  Typography,
  Descriptions,
  Tag,
  Table,
  Switch,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Empty,
  Tooltip,
  Image,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
  ExportOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  StoreDisplayGroupPlanogram,
  StoreDisplayGroupDetailAlgorithm,
  StoreDisplayGroupDetailManual,
  ManualResultEditForm,
} from "../../../types/storeDisplayGroupPlanogram";
import StoreDisplayGroupPlanogramService from "../../../services/storeDisplayGroupPlanogramService";
import NetworkError from "../../../components/common/NetworkError";
import EmptyState from "../../../components/common/EmptyState";
import FilterPanel from "../../../components/common/FilterPanel";
import { handleApiResponse } from "../../../utils/responseTransformer";
import {
  isRetryableError,
  sanitizeErrorForDisplay,
} from "../../../utils/errorHandler";
import dayjs from "dayjs";
import ShelfPreviewDialog from "../../../components/common/ShelfEditor";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StoreDisplayGroupPlanogramDetail: React.FC = () => {
  const { storeDisplayGroupCode, planogramCode, source } = useParams<{
    storeDisplayGroupCode: string;
    planogramCode: string;
    source: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // Loading states
  const [loading, setLoading] = useState<boolean>(true);
  const [algorithmLoading, setAlgorithmLoading] = useState<boolean>(false);
  const [manualLoading, setManualLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const [shelfPreviewDialogVisible, setShelfPreviewDialogVisible] =
    useState<boolean>(false);
  const [shelfPreviewDialogIsPreview, setShelfPreviewDialogIsPreview] =
    useState<boolean>(true);
  const [shelfPreviewDialogData, setShelfPreviewDialogData] =
    useState<any>(null);

  const handlePreview = (record: any, isPreview: boolean) => {
    try {
      // TODO: 实现在线预览功能
      setShelfPreviewDialogVisible(true);
      setShelfPreviewDialogData(record);
      setShelfPreviewDialogIsPreview(!!isPreview);
      // 实际应该调用预览 API 或打开预览组件
    } catch (error) {
      message.error(t("storeGroup.planogram.messages.previewFailed"));
      console.error("Preview failed:", error);
    }
  };

  // Data states
  const [planogramDetail, setPlanogramDetail] =
    useState<StoreDisplayGroupPlanogram | null>(null);
  const [algorithmData, setAlgorithmData] = useState<
    StoreDisplayGroupDetailAlgorithm[]
  >([]);
  const [manualData, setManualData] = useState<StoreDisplayGroupDetailManual[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);

  // Filter states (removed - no longer needed)
  // const [algorithmFilters, setAlgorithmFilters] = useState<PlanogramDetailFilters>({});
  // const [manualFilters, setManualFilters] = useState<PlanogramDetailFilters>({});

  // Edit states
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] =
    useState<StoreDisplayGroupDetailManual | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isAddMode, setIsAddMode] = useState<boolean>(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<string>("basicInfo");

  // Load planogram detail
  const loadPlanogramDetail = useCallback(async () => {
    if (!planogramCode) return;

    setLoading(true);
    setError(null);

    try {
      const response =
        await StoreDisplayGroupPlanogramService.getPlanogramDetail(
          planogramCode
        );
      const detail = handleApiResponse(response, "loadPlanogramDetail");
      setPlanogramDetail(detail as StoreDisplayGroupPlanogram);

      if (detail) {
        setAlgorithmData(
          (detail as StoreDisplayGroupPlanogram).algorithmDetails || []
        );
        setManualData(
          (detail as StoreDisplayGroupPlanogram).manualDetails || []
        );
      }
    } catch (error) {
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
      setPlanogramDetail(null);
    } finally {
      setLoading(false);
    }
  }, [planogramCode]);

  // Initial data load
  useEffect(() => {
    loadPlanogramDetail();
  }, [loadPlanogramDetail]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (source === "list") {
      navigate(`/store-display-groups/planogram/list`);
    } else {
      // 默认返回到门店陈列分组的planogram列表页面
      navigate(`/store-display-groups/${storeDisplayGroupCode}/planogram`);
    }
  }, [navigate, storeDisplayGroupCode, source]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    try {
      await loadPlanogramDetail();
      message.success(t("storeDisplayGroupPlanogram.messages.loadSuccess"));
    } catch (error) {
      message.error(t("storeDisplayGroupPlanogram.messages.loadFailed"));
    }
  }, [loadPlanogramDetail, t]);

  // Handle retry for network errors
  const handleRetry = useCallback(() => {
    loadPlanogramDetail();
  }, [loadPlanogramDetail]);

  // Handle edit mode toggle
  const handleEditModeToggle = useCallback(
    (enabled: boolean) => {
      setEditMode(enabled);
      if (!enabled) {
        setEditingRecord(null);
        setIsEditModalVisible(false);
        setIsAddMode(false);
        form.resetFields();
      }
    },
    [form]
  );

  // Handle add new manual result
  const handleAddNew = useCallback(() => {
    setIsAddMode(true);
    setEditingRecord(null);
    setIsEditModalVisible(true);
    form.resetFields();
  }, [form]);

  // Format datetime display
  const formatDateTime = (timestamp: string): string => {
    return timestamp ? dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss") : "-";
  };

  // Status mapping function
  const getStatusKey = (status: string): string => {
    const statusMap: Record<string, string> = {
      草稿: "Draft",
      审核中: "PENDING",
      审核拒绝: "REJECTED",
      生效中: "ACTIVE",
      已归档: "ARCHIVED",
    };
    return statusMap[status] || status;
  };

  // console.log("****", planogramDetail);
  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show error state
  if (error && isRetryableError({ message: error })) {
    return (
      <div className="page-container">
        <NetworkError onRetry={handleRetry} message={error} />
      </div>
    );
  }

  // Show planogram not found
  if (!planogramDetail) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h2>{t("storeDisplayGroupPlanogram.messages.notFound")}</h2>
        <Button type="primary" onClick={handleBack}>
          {t("common.back")}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: "24px" }}
      >
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              {t("common.back")}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {t("storeDisplayGroupPlanogram.detailTitle")}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t("storeDisplayGroupPlanogram.actions.refreshData")}
            </Button>
            {/* <Button icon={<ExportOutlined />}>
              {t('storeDisplayGroupPlanogram.actions.exportData')}
            </Button> */}
          </Space>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Basic Information Tab */}
        <TabPane
          key="basicInfo"
          tab={t("storeDisplayGroupPlanogram.tabs.basicInfo")}
        >
          {/* Basic Information Card */}
          <Card
            title={t("storeDisplayGroupPlanogram.basicInfo.title")}
            style={{ marginBottom: "16px" }}
          >
            <Descriptions column={3} bordered>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.code")}
              >
                {planogramDetail.code}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.source")}
              >
                <Tag
                  color={
                    planogramDetail.source === "algorithm" ? "blue" : "green"
                  }
                >
                  {t(
                    `storeDisplayGroupPlanogram.sourceType.${planogramDetail.source}`
                  )}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.status")}
              >
                <Tag
                  color={
                    planogramDetail.status === "生效中"
                      ? "success"
                      : planogramDetail.status === "审核中"
                      ? "processing"
                      : planogramDetail.status === "审核拒绝"
                      ? "error"
                      : planogramDetail.status === "草稿"
                      ? "default"
                      : "warning"
                  }
                >
                  {t(
                    `storeDisplayGroupPlanogram.statusType.${getStatusKey(
                      planogramDetail.status
                    )}`
                  )}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.itemCount")}
              >
                {planogramDetail.itemCount}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.shelfCount")}
              >
                {planogramDetail.shelfCount}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.createTime")}
              >
                {formatDateTime(planogramDetail.gmtCreate || "")}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("storeDisplayGroupPlanogram.basicInfo.updateTime")}
              >
                {formatDateTime(planogramDetail.gmtModified || "")}
              </Descriptions.Item>
            </Descriptions>

            {planogramDetail.description && (
              <div style={{ marginTop: "16px" }}>
                <Text strong>
                  {t("storeDisplayGroupPlanogram.basicInfo.description")}:{" "}
                </Text>
                <Text>{planogramDetail.description}</Text>
              </div>
            )}

            {/* Images Display Section */}
            {(planogramDetail.imageUrl ||
              planogramDetail.manualImageUrl ||
              planogramDetail.manualImageByInventoryUrl) && (
              <div style={{ marginTop: "24px" }}>
                <Text strong>
                  {t("storeDisplayGroupPlanogram.basicInfo.images")}:{" "}
                </Text>
                <div style={{ marginTop: "12px" }}>
                  <Row gutter={[16, 16]}>
                    {planogramDetail.imageUrl && (
                      <Col>
                        <div style={{ textAlign: "center" }}>
                          <Image
                            src={`${planogramDetail.imageUrl}?x-oss-process=image/resize,w_200,h_150`}
                            alt={t(
                              "storeDisplayGroupPlanogram.basicInfo.algorithmImage"
                            )}
                            loading="lazy"
                            width={200}
                            height={150}
                            style={{
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #d9d9d9",
                            }}
                            fallback={planogramDetail.imageUrl}
                            preview={{ src: planogramDetail.imageUrl }}
                          />
                          <div style={{ marginTop: "8px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {t(
                                "storeDisplayGroupPlanogram.basicInfo.algorithmImage"
                              )}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    )}
                    {planogramDetail.manualImageUrl && (
                      <Col>
                        <div style={{ textAlign: "center" }}>
                          <Image
                            src={`${planogramDetail.manualImageUrl}?x-oss-process=image/resize,w_200,h_150`}
                            alt={t(
                              "storeDisplayGroupPlanogram.basicInfo.manualImage"
                            )}
                            loading="lazy"
                            width={200}
                            height={150}
                            style={{
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #d9d9d9",
                            }}
                            fallback={planogramDetail.manualImageUrl}
                            preview={{ src: planogramDetail.manualImageUrl }}
                          />
                          <div style={{ marginTop: "8px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {t(
                                "storeDisplayGroupPlanogram.basicInfo.manualImage"
                              )}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    )}
                    {planogramDetail.manualImageByInventoryUrl && (
                      <Col>
                        <div style={{ textAlign: "center" }}>
                          <Image
                            src={`${planogramDetail.manualImageByInventoryUrl}?x-oss-process=image/resize,w_200,h_150`}
                            alt={t(
                              "storeDisplayGroupPlanogram.basicInfo.manualImageByInventoryUrl"
                            )}
                            loading="lazy"
                            width={200}
                            height={150}
                            style={{
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #d9d9d9",
                            }}
                            fallback={planogramDetail.manualImageByInventoryUrl}
                            preview={{
                              src: planogramDetail.manualImageByInventoryUrl,
                            }}
                          />
                          <div style={{ marginTop: "8px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {t(
                                "storeDisplayGroupPlanogram.basicInfo.manualImageByInventoryUrl"
                              )}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              </div>
            )}
          </Card>

          {/* To Be Delisted Items Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
            {/* Algorithm Calculated To Be Delisted Items */}
            <Col span={12}>
              <Card
                title={t(
                  "storeDisplayGroupPlanogram.toBeDelistItems.algorithmItems"
                )}
                size="small"
                style={{ height: "100%" }}
              >
                {planogramDetail.toBeDelistAlgItems &&
                planogramDetail.toBeDelistAlgItems.length > 0 ? (
                  <Table
                    columns={[
                      {
                        title: t(
                          "storeDisplayGroupPlanogram.toBeDelistItems.itemCode"
                        ),
                        dataIndex: "code",
                        key: "code",
                        width: 120,
                        render: (code: string) => (
                          <Text strong style={{ color: "#1890ff" }}>
                            {code}
                          </Text>
                        ),
                      },
                      {
                        title: t(
                          "storeDisplayGroupPlanogram.toBeDelistItems.itemName"
                        ),
                        dataIndex: "name",
                        key: "name",
                        ellipsis: { showTitle: false },
                        render: (name: string) => (
                          <Tooltip title={name}>
                            <Text>{name}</Text>
                          </Tooltip>
                        ),
                      },
                      {
                        title: t(
                          "storeDisplayGroupPlanogram.toBeDelistItems.ownerCode"
                        ),
                        dataIndex: "ownerCode",
                        key: "ownerCode",
                        width: 100,
                        render: (ownerCode: string) => ownerCode || "-",
                      },
                    ]}
                    dataSource={planogramDetail.toBeDelistAlgItems}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 1200, y: 500 }}
                    locale={{
                      emptyText: (
                        <EmptyState
                          type="default"
                          title={t(
                            "storeDisplayGroupPlanogram.toBeDelistItems.noAlgorithmItems"
                          )}
                          description={t(
                            "storeDisplayGroupPlanogram.toBeDelistItems.noAlgorithmItems"
                          )}
                        />
                      ),
                    }}
                  />
                ) : (
                  <EmptyState
                    type="default"
                    title={t(
                      "storeDisplayGroupPlanogram.toBeDelistItems.noAlgorithmItems"
                    )}
                    description={t(
                      "storeDisplayGroupPlanogram.toBeDelistItems.noAlgorithmItems"
                    )}
                  />
                )}
              </Card>
            </Col>

            {/* Manually Processed To Be Delisted Items */}
            <Col span={12}>
              <Card
                title={t(
                  "storeDisplayGroupPlanogram.toBeDelistItems.manualItems"
                )}
                size="small"
                style={{ height: "100%" }}
              >
                {planogramDetail.toBeDelistManulItems &&
                planogramDetail.toBeDelistManulItems.length > 0 ? (
                  <Table
                    columns={[
                      {
                        title: t(
                          "storeDisplayGroupPlanogram.toBeDelistItems.itemCode"
                        ),
                        dataIndex: "code",
                        key: "code",
                        width: 120,
                        render: (code: string) => (
                          <Text strong style={{ color: "#52c41a" }}>
                            {code}
                          </Text>
                        ),
                      },
                      {
                        title: t(
                          "storeDisplayGroupPlanogram.toBeDelistItems.itemName"
                        ),
                        dataIndex: "name",
                        key: "name",
                        ellipsis: { showTitle: false },
                        render: (name: string) => (
                          <Tooltip title={name}>
                            <Text>{name}</Text>
                          </Tooltip>
                        ),
                      },
                      {
                        title: t(
                          "storeDisplayGroupPlanogram.toBeDelistItems.ownerCode"
                        ),
                        dataIndex: "ownerCode",
                        key: "ownerCode",
                        width: 100,
                        render: (ownerCode: string) => ownerCode || "-",
                      },
                    ]}
                    dataSource={planogramDetail.toBeDelistManulItems}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 1200, y: 500 }}
                    locale={{
                      emptyText: (
                        <EmptyState
                          type="default"
                          title={t(
                            "storeDisplayGroupPlanogram.toBeDelistItems.noManualItems"
                          )}
                          description={t(
                            "storeDisplayGroupPlanogram.toBeDelistItems.noManualItems"
                          )}
                        />
                      ),
                    }}
                  />
                ) : (
                  <EmptyState
                    type="default"
                    title={t(
                      "storeDisplayGroupPlanogram.toBeDelistItems.noManualItems"
                    )}
                    description={t(
                      "storeDisplayGroupPlanogram.toBeDelistItems.noManualItems"
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>

          {/* Statistics Card */}
          {/* <Card title="统计信息">
            <Row gutter={[32, 16]}>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                    {algorithmData.length}
                  </div>
                  <div style={{ color: '#666' }}>{t('storeDisplayGroupPlanogram.statistics.algorithmResultsCount')}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                    {manualData.length}
                  </div>
                  <div style={{ color: '#666' }}>{t('storeDisplayGroupPlanogram.statistics.manualResultsCount')}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                    {Math.abs(algorithmData.length - manualData.length)}
                  </div>
                  <div style={{ color: '#666' }}>{t('storeDisplayGroupPlanogram.statistics.differenceCount')}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                    {manualData.length > 0 ? ((manualData.length / Math.max(algorithmData.length, 1)) * 100).toFixed(1) : 0}%
                  </div>
                  <div style={{ color: '#666' }}>{t('storeDisplayGroupPlanogram.statistics.manualAdjustmentRate')}</div>
                </div>
              </Col>
            </Row>
          </Card> */}
        </TabPane>

        {/* Algorithm Results Tab */}
        <TabPane
          key="algorithmResults"
          tab={t("storeDisplayGroupPlanogram.tabs.algorithmResults")}
        >
          <Card title={t("storeDisplayGroupPlanogram.algorithmResults.title")}>
            {/* Algorithm Results Table */}
            <Table
              columns={
                [
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.shelfCode"
                    ),
                    dataIndex: "shelfCode",
                    key: "shelfCode",
                    width: 120,
                    sorter: (a, b) => a.shelfCode.localeCompare(b.shelfCode),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.layer"
                    ),
                    dataIndex: "layer",
                    key: "layer",
                    width: 80,
                    sorter: (a, b) => a.layer.localeCompare(b.layer),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.shelfIndex"
                    ),
                    dataIndex: "shelfIndex",
                    key: "shelfIndex",
                    width: 100,
                    align: "center",
                    sorter: (a, b) => a.shelfIndex - b.shelfIndex,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.ownerCode"
                    ),
                    dataIndex: "ownerCode",
                    key: "ownerCode",
                    width: 120,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.itemCode"
                    ),
                    dataIndex: "itemCode",
                    key: "itemCode",
                    width: 120,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.seqOnShelfLayer"
                    ),
                    dataIndex: "seqOnShelfLayer",
                    key: "seqOnShelfLayer",
                    width: 100,
                    align: "center",
                    sorter: (a, b) => a.seqOnShelfLayer - b.seqOnShelfLayer,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.facingCount"
                    ),
                    dataIndex: "facingCount",
                    key: "facingCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#1890ff" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.verticalCount"
                    ),
                    dataIndex: "verticalCount",
                    key: "verticalCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#52c41a" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.depthCount"
                    ),
                    dataIndex: "depthCount",
                    key: "depthCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#faad14" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.pdqCount"
                    ),
                    dataIndex: "pdqCount",
                    key: "pdqCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#722ed1" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t("common.createTime"),
                    dataIndex: "gmtCreate",
                    key: "gmtCreate",
                    width: 180,
                    render: formatDateTime,
                  },
                ] as ColumnsType<StoreDisplayGroupDetailAlgorithm>
              }
              dataSource={algorithmData}
              rowKey="id"
              loading={algorithmLoading}
              pagination={false}
              scroll={{ x: 1200, y: 500 }}
              size="small"
              locale={{
                emptyText:
                  algorithmData.length === 0 && !algorithmLoading ? (
                    <EmptyState
                      type="default"
                      description={t(
                        "storeDisplayGroupPlanogram.messages.noAlgorithmData"
                      )}
                    />
                  ) : undefined,
              }}
            />
          </Card>
        </TabPane>

        {/* Manual Results Tab */}
        <TabPane
          key="manualResults"
          tab={t("storeDisplayGroupPlanogram.tabs.manualResults")}
        >
          <Card title={t("storeDisplayGroupPlanogram.manualResults.title")}>
            <div
              style={{
                marginBottom: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space>
                {/* <span>
                  {t("storeDisplayGroupPlanogram.manualResults.editMode")}:
                </span>
                <Switch
                  checked={editMode}
                  onChange={handleEditModeToggle}
                  checkedChildren={t(
                    "storeDisplayGroupPlanogram.manualResults.enableEdit"
                  )}
                  unCheckedChildren={t(
                    "storeDisplayGroupPlanogram.manualResults.disableEdit"
                  )}
                /> */}

                <Button
                  type="primary"
                  disabled={planogramDetail.status !== "ACTIVE" || planogramDetail.source === 'benchmark'}
                  onClick={() => handlePreview(planogramDetail, false)}
                >
                  {t("common.edit")}
                </Button>
              </Space>
            </div>

            {/* Manual Results Table */}
            <Table
              columns={
                [
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.shelfCode"
                    ),
                    dataIndex: "shelfCode",
                    key: "shelfCode",
                    width: 120,
                    sorter: (a, b) => a.shelfCode.localeCompare(b.shelfCode),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.layer"
                    ),
                    dataIndex: "layer",
                    key: "layer",
                    width: 80,
                    sorter: (a, b) => a.layer.localeCompare(b.layer),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.shelfIndex"
                    ),
                    dataIndex: "shelfIndex",
                    key: "shelfIndex",
                    width: 100,
                    align: "center",
                    sorter: (a, b) => a.shelfIndex - b.shelfIndex,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.ownerCode"
                    ),
                    dataIndex: "ownerCode",
                    key: "ownerCode",
                    width: 120,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.itemCode"
                    ),
                    dataIndex: "itemCode",
                    key: "itemCode",
                    width: 120,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.seqOnShelfLayer"
                    ),
                    dataIndex: "seqOnShelfLayer",
                    key: "seqOnShelfLayer",
                    width: 100,
                    align: "center",
                    sorter: (a, b) => a.seqOnShelfLayer - b.seqOnShelfLayer,
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.facingCount"
                    ),
                    dataIndex: "facingCount",
                    key: "facingCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#1890ff" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.verticalCount"
                    ),
                    dataIndex: "verticalCount",
                    key: "verticalCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#52c41a" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.depthCount"
                    ),
                    dataIndex: "depthCount",
                    key: "depthCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#faad14" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t(
                      "storeDisplayGroupPlanogram.algorithmResults.pdqCount"
                    ),
                    dataIndex: "pdqCount",
                    key: "pdqCount",
                    width: 100,
                    align: "center",
                    render: (count: number) => (
                      <Text strong style={{ color: "#722ed1" }}>
                        {count}
                      </Text>
                    ),
                  },
                  {
                    title: t("common.createTime"),
                    dataIndex: "gmtCreate",
                    key: "gmtCreate",
                    width: 180,
                    render: formatDateTime,
                  },
                  {
                    title: t("common.modifyTime"),
                    dataIndex: "gmtModified",
                    key: "gmtModified",
                    width: 180,
                    render: formatDateTime,
                  },
                ] as ColumnsType<StoreDisplayGroupDetailManual>
              }
              dataSource={manualData}
              rowKey="id"
              loading={manualLoading}
              pagination={false}
              scroll={{ x: 1200, y: 500 }}
              size="small"
              locale={{
                emptyText:
                  manualData.length === 0 && !manualLoading ? (
                    <EmptyState
                      type="default"
                      description={t(
                        "storeDisplayGroupPlanogram.messages.noManualData"
                      )}
                      onAction={editMode ? handleAddNew : undefined}
                      actionText={
                        editMode
                          ? t("storeDisplayGroupPlanogram.manualResults.addNew")
                          : undefined
                      }
                    />
                  ) : undefined,
              }}
            />
          </Card>
        </TabPane>

        {/* Results Comparison Tab */}
        {/* <TabPane key="comparison" tab={t('storeDisplayGroupPlanogram.tabs.comparison')}> */}
        {/* Component will be implemented later */}
        {/* <div>Results Comparison Content</div> */}
        {/* </TabPane> */}
      </Tabs>

      <ShelfPreviewDialog
        visible={shelfPreviewDialogVisible}
        setVisible={setShelfPreviewDialogVisible}
        data={shelfPreviewDialogData}
        origin={"planList"}
        canEdit={!shelfPreviewDialogIsPreview}
        isVersion2={true}
      />
    </div>
  );
};

export default StoreDisplayGroupPlanogramDetail;
